import { useRichTextEditorContext } from '@mantine/tiptap';
import { IconTypography } from '@tabler/icons-react';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';
import DropdownControl, {
  DropdownControlKind,
} from 'pages/revisions-module/template-editor/editors/template/toolbar/controls/DropdownControl';
import SimpleControl from 'pages/revisions-module/template-editor/editors/template/toolbar/controls/SimpleControl';
import { useEffect, useMemo, useState } from 'react';

interface FontFamily {
  name: string;
  label: string;
  cssValue: string;
  preview: string;
  source: 'default' | 'google';
}

const DEFAULT_FONT_FAMILIES: FontFamily[] = [
  {
    name: 'serif',
    label: 'Serif',
    cssValue: 'serif',
    preview: 'Aa',
    source: 'default',
  },
  {
    name: 'sans-serif',
    label: 'Sans Serif',
    cssValue: 'sans-serif',
    preview: 'Aa',
    source: 'default',
  },
  {
    name: 'monospace',
    label: 'Monospace',
    cssValue: 'monospace',
    preview: 'Aa',
    source: 'default',
  },
];

/**
 * Converts font name into CSS `font-family` value with a fallback.
 *
 * @param fontName Selected font family name.
 * @returns CSS font family value.
 */
function fontToCssValue(fontName: string) {
  return `'${fontName}', sans-serif`;
}

/**
 * Normalizes font family value for reliable comparisons.
 *
 * @param value Raw CSS font family value.
 * @returns Lowercased primary font name without quotes.
 */
function normalizeFontValue(value?: string) {
  if (!value) {
    return '';
  }

  return value.split(',')[0]?.replace(/["']/g, '').trim().toLowerCase() ?? '';
}

/**
 * Control which allows the user to select a font family.
 */
export default function FontStyle({ kind }: { kind: DropdownControlKind }) {
  const { editor } = useRichTextEditorContext();
  const { visual } = useTemplateEditorData();

  const allFontFamilies = useMemo(
    () => [
      ...DEFAULT_FONT_FAMILIES,
      ...(visual.googleFonts ?? []).map(({ fontName }) => ({
        name: `google-${fontName}`,
        label: `${fontName} (Google)`,
        cssValue: fontToCssValue(fontName),
        preview: 'Aa',
        source: 'google' as const,
      })),
    ],
    [visual.googleFonts]
  );

  const defaultVisualFont = useMemo(() => {
    const visualFontFamily = normalizeFontValue(visual.fontFamily);
    return (
      allFontFamilies.find((font) => normalizeFontValue(font.cssValue) === visualFontFamily) ?? DEFAULT_FONT_FAMILIES[1]
    );
  }, [allFontFamilies, visual.fontFamily]);

  const resolvedActiveFont = useMemo(() => {
    const currentFontFamily = normalizeFontValue(editor?.getAttributes('textStyle').fontFamily);
    const found = allFontFamilies.find((font) => normalizeFontValue(font.cssValue) === currentFontFamily);
    return found ?? defaultVisualFont;
  }, [allFontFamilies, defaultVisualFont, editor?.state.selection]);

  const [activeFont, setActiveFont] = useState<FontFamily>(resolvedActiveFont);

  useEffect(() => {
    setActiveFont(resolvedActiveFont);
  }, [resolvedActiveFont]);

  /**
   * Applies a selected font family or unsets it if the same item is clicked.
   *
   * @param oldFont Previously active font.
   * @param newFont Font selected by the user.
   */
  const fontClicked = (oldFont: FontFamily, newFont: FontFamily) => {
    window.requestAnimationFrame(() => {
      if (newFont.cssValue === oldFont.cssValue) {
        // If clicking the same font, unset it (revert to default)
        editor?.chain().focus().unsetFontFamily().run();
        setActiveFont(defaultVisualFont);
      } else {
        // Set the new font family using FontFamily extension
        editor?.chain().focus().setFontFamily(newFont.cssValue).run();
        setActiveFont(newFont);
      }
    });
  };

  return (
    <DropdownControl kind={kind} label="Písmo" text={activeFont.label} icon={<IconTypography />}>
      {allFontFamilies.map((font, index) => (
        <SimpleControl
          key={`FontStyle.${font.name}`}
          kind="menu-item"
          icon={
            <span
              style={{
                fontFamily: font.cssValue,
                fontWeight: 'bold',
                fontSize: '14px',
                width: '16px',
                textAlign: 'center',
                display: 'inline-block',
              }}
            >
              {font.preview}
            </span>
          }
          label={font.label}
          onClick={() => fontClicked(activeFont, font)}
        />
      ))}
    </DropdownControl>
  );
}
